import React from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { ButtonAlterar } from '../../shared/buttons';
import Spinner from '../../shared/spinner';
import { Comunicado } from '../../shared/model/comunicado';
import { format } from 'date-fns';
import { faEye, faFilter, faRefresh, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ComunicadoService from '../../services/comunicadoService';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import { useSlug } from '../hooks/UseSlug'; // 1. Import do Hook

// Interface para tipos de comunicado
interface TipoComunicado {
  id: string;
  descricao: string;
}

function ComunicadosComponent() {
  useVerificaPermissao();
  const navigate = useNavigate();
  const slug = useSlug(); // 2. Obtendo o slug

  const [loading, setLoading] = useState(true);
  const [comunicados, setComunicados] = useState<Comunicado[]>([]);
  const [refresh, setRefresh] = useState(false);

  // Estados para filtros
  const [filtros, setFiltros] = useState(false);
  const [nomeComunicadoFiltro, setNomeComunicadoFiltro] = useState('');
  const [tipoComunicadoFiltro, setTipoComunicadoFiltro] = useState('');
  const [dataInicioFiltro, setDataInicioFiltro] = useState<Date | undefined>();
  const [dataFimFiltro, setDataFimFiltro] = useState<Date | undefined>();
  const [tiposComunicados, setTiposComunicados] = useState<TipoComunicado[]>([]);

  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const tamanhoPagina = 10;

  const comunicadoService: ComunicadoService = new ComunicadoService();

  // Carregar tipos de comunicado para o filtro
  useEffect(() => {
    const carregarTiposComunicado = async () => {
      try {
        const response = await comunicadoService.listarTodosTipoComunicadoAtivo();
        setTiposComunicados(response.data);
      } catch (err) {
        console.error('Erro ao carregar tipos de comunicado:', err);
        // Fallback com dados estáticos em caso de erro
        setTiposComunicados([
          { id: '1', descricao: 'Tipo 1' },
          { id: '2', descricao: 'Tipo 2' },
          { id: '13', descricao: 'Tipo 13' }
        ]);
      }
    };
    
    carregarTiposComunicado();
  }, []);


  // Listar comunicados com paginação e filtros
  const listarTodosComunicados = async (page = paginaAtual) => {
    try {
      setLoading(true);
      
      // Preparar parâmetros de filtro
      const filtrosParams = {
        nomeComunicado: nomeComunicadoFiltro || undefined,
        tipoComunicado: tipoComunicadoFiltro || undefined,
        dataInicio: dataInicioFiltro ? dataInicioFiltro.toISOString() : undefined,
        dataFim: dataFimFiltro ? dataFimFiltro.toISOString() : undefined
      };
      
      const responseList = await comunicadoService.listarTodosComunicados(
        page, 
        tamanhoPagina, 
        filtrosParams // Passe os filtros para o service
      );
      
      setComunicados(responseList.data.content);
      setTotalPaginas(responseList.data.totalPages);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  // Função para aplicar filtros
  const aplicarFiltros = () => {
    setPaginaAtual(0); // Resetar para primeira página
    listarTodosComunicados(0);
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    setNomeComunicadoFiltro('');
    setTipoComunicadoFiltro('');
    setDataInicioFiltro(undefined);
    setDataFimFiltro(undefined);
    setPaginaAtual(0);
    listarTodosComunicados(0);
  };

  useEffect(() => {
    listarTodosComunicados();
  }, [refresh, paginaAtual]);

  // Função para renderizar os botões de página numerados
  const renderizarBotoesPaginacao = () => {
    const botoes = [];
    const maxBotoes = 7; // máximo de botões de página a mostrar (excluindo '...' e primeiras/últimas)
    let inicio = Math.max(0, paginaAtual - Math.floor(maxBotoes / 2));
    let fim = inicio + maxBotoes - 1;

    if (fim >= totalPaginas) {
      fim = totalPaginas - 1;
      inicio = Math.max(0, fim - maxBotoes + 1);
    }

    // Botão para ir para a primeira página
    if (inicio > 0) {
      botoes.push(
        <Button key={0} variant="link" onClick={() => setPaginaAtual(0)}>1</Button>
      );
      if (inicio > 1) {
        botoes.push(<span key="inicio-ellipsis" style={{ margin: '0 5px' }}>...</span>);
      }
    }

    // Botões centrais da paginação
    for (let i = inicio; i <= fim; i++) {
      botoes.push(
        <Button
          key={i}
          variant={i === paginaAtual ? "primary" : "link"}
          onClick={() => setPaginaAtual(i)}
          style={{ fontWeight: i === paginaAtual ? 'bold' : 'normal' }}
        >
          {i + 1}
        </Button>
      );
    }

    // Botão para ir para a última página
    if (fim < totalPaginas - 1) {
      if (fim < totalPaginas - 2) {
        botoes.push(<span key="fim-ellipsis" style={{ margin: '0 5px' }}>...</span>);
      }
      botoes.push(
        <Button key={totalPaginas - 1} variant="link" onClick={() => setPaginaAtual(totalPaginas - 1)}>
          {totalPaginas}
        </Button>
      );
    }

    return botoes;
  };

  return (
    <>
      <div className="position-relative m-md-3">
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
          <h5>Comunicados</h5>
          <Button
            // 3. Rota corrigida com Slug
            onClick={() => navigate(`/${slug}/comunicados/inclusao`, { state: { id: null } })}
            id="App-button-cor-principal-escura"
          >
            + Novo Comunicado
          </Button>
        </div>

        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
          <Button id="App-button-cor-principal-escura" onClick={() => setFiltros(!filtros)}>
            <FontAwesomeIcon icon={faFilter} style={{ marginRight: '10px' }} />
            Filtros
          </Button>
        </div>
        
        {filtros && (
          <>
            <Row className="mb-4" style={{ marginTop: '15px' }}>
              <Form.Group as={Col} md='6' controlId="formNomeComunicado">
                <Form.Label>Nome do Comunicado</Form.Label>
                <Form.Control 
                  type="text" 
                  value={nomeComunicadoFiltro} 
                  onChange={(e) => setNomeComunicadoFiltro(e.target.value)} 
                  placeholder="Digite o nome do comunicado"
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} md='4' controlId="formTipoComunicado">
                <Form.Label>Tipo de Comunicado</Form.Label>
                <Form.Select 
                  value={tipoComunicadoFiltro} 
                  onChange={(e) => setTipoComunicadoFiltro(e.target.value)}
                >
                  <option value="">Selecione um tipo de comunicado</option>
                  {tiposComunicados.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.id + ' - ' + item.descricao}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md='3'>
                <Form.Label>Data de Início</Form.Label>
                <Form.Control 
                  type="date" 
                  value={dataInicioFiltro ? dataInicioFiltro.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const newDate = new Date(e.target.value);
                      newDate.setHours(newDate.getHours() + newDate.getTimezoneOffset() / 60);
                      setDataInicioFiltro(newDate);
                    } else {
                      setDataInicioFiltro(undefined);
                    }
                  }}
                />
              </Form.Group>
              <Form.Group as={Col} md='1' style={{ display: 'flex', marginTop: '30px', width: '1%' }}>
                <strong style={{ marginTop: '8px' }}>a</strong>
              </Form.Group>
              <Form.Group as={Col} md='3'>
                <Form.Label>Data de Fim</Form.Label>
                <Form.Control 
                  type="date" 
                  value={dataFimFiltro ? dataFimFiltro.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const newDate = new Date(e.target.value);
                      newDate.setHours(newDate.getHours() + newDate.getTimezoneOffset() / 60);
                      setDataFimFiltro(newDate);
                    } else {
                      setDataFimFiltro(undefined);
                    }
                  }}
                />
              </Form.Group>
            </Row>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button variant="secondary" onClick={limparFiltros}>
                <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '10px' }} />
                Limpar Filtros
              </Button>
              <Button id="App-button-cor-principal-escura" onClick={aplicarFiltros}>
                Aplicar Filtros
              </Button>
            </div>
          </>
        )}
          
        {loading ? <Spinner loading={loading} /> : (
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th scope="col" id="App-header-table">Código</th>
                  <th scope="col" id="App-header-table">Nome</th>
                  <th scope="col" id="App-header-table">Data Início</th>
                  <th scope="col" id="App-header-table">Data Fim</th>
                  <th scope="col" id="App-header-table">Visualizar</th>
                  <th scope="col" id="App-header-table">Alteração</th>
                </tr>
              </thead>
              <tbody>
                {comunicados.length > 0 ? (
                  comunicados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.nomeComunicado}</td>
                      <td>{item.dtInicio ? format(new Date(item.dtInicio), 'dd/MM/yyyy') : ''}</td>
                      <td>{item.dtFim ? format(new Date(item.dtFim), 'dd/MM/yyyy') : ''}</td>
                      <td>
                        <Button
                          style={{ background: 'grey', border: 'grey', marginLeft: '5px' }}
                          // 4. Link de visualização com Slug
                          onClick={() => navigate(`/${slug}/comunicados-visualizacao`, { state: { id: item.id } })}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                      </td>
                      <td>
                        {/* 5. Link de alteração com Slug */}
                        <Link to={`/${slug}/comunicados/alteracao`} state={{ id: item.id }}>
                          <ButtonAlterar />
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">
                      Nenhum comunicado encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalPaginas > 1 && (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Button
                  onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 0))}
                  disabled={paginaAtual === 0}
                  style={{ marginRight: '10px' }}
                >
                  «
                </Button>

                {renderizarBotoesPaginacao()}

                <Button
                  onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas - 1))}
                  disabled={paginaAtual >= totalPaginas - 1}
                  style={{ marginLeft: '10px' }}
                >
                  »
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default ComunicadosComponent;